import { SLOT_SYMBOL_IMAGES } from "../../../../../static/Constants";
import { colors, borderRadiusPx, customShadows, FIVESLOT_COLOR_THEME } from "../../../../../theme";

/**
 * 5-Slot Detail Page Theme & Layout Config (Centralized Tokens)
 */
export const FIVE_SLOT_DETAILS_THEME = {
  pageBg: colors.dashboard.canvas,
  cardMaxWidth: 1040,
  containerMaxWidth: "lg",
  headerBorderRadius: borderRadiusPx.xl,
  machineBorderRadius: borderRadiusPx.xxl,
  shadow: customShadows.card,
  hoverShadow: customShadows.cardHover,
  colorTheme: FIVESLOT_COLOR_THEME,
};

/**
 * Centralized Progressive Jackpot Tier Themes (Mega, Major, Mini)
 * Styled with light, clean surfaces and vibrant lottery accent borders & text
 */
export const JACKPOT_TIER_THEMES = {
  mega: {
    id: "mega",
    label: "👑 MEGA JACKPOT",
    background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
    border: "1.5px solid #fca5a5",
    borderColor: "#fca5a5",
    boxShadow: "0 2px 8px rgba(239, 68, 68, 0.12)",
    titleColor: "#dc2626",
    valueColor: "#991b1b",
    borderRadius: borderRadiusPx.md || "10px",
  },
  major: {
    id: "major",
    label: "💎 MAJOR JACKPOT",
    background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
    border: "1.5px solid #d8b4fe",
    borderColor: "#d8b4fe",
    boxShadow: "0 2px 8px rgba(168, 85, 247, 0.12)",
    titleColor: "#7c3aed",
    valueColor: "#581c87",
    borderRadius: borderRadiusPx.md || "10px",
  },
  mini: {
    id: "mini",
    label: "🔔 MINI JACKPOT",
    background: "linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)",
    border: "1.5px solid #fde047",
    borderColor: "#fde047",
    boxShadow: "0 2px 8px rgba(234, 179, 8, 0.12)",
    titleColor: "#ca8a04",
    valueColor: "#854d0e",
    borderRadius: borderRadiusPx.md || "10px",
  },
};

/**
 * Side Badge Numbers with Casino Neon Colors
 */
export const SIDE_BADGES = [
  { num: "4", color: colors.red[500] || "#ef4444" },
  { num: "2", color: colors.blue[500] || "#3b82f6" },
  { num: "9", color: colors.emerald[500] || "#22c55e" },
  { num: "6", color: colors.amber[500] || "#eab308" },
  { num: "1", color: colors.purple[500] || "#a855f7" },
  { num: "7", color: "#f97316" },
  { num: "8", color: colors.cyan[500] || "#06b6d4" },
  { num: "3", color: "#ec4899" },
  { num: "5", color: colors.blue[500] || "#3b82f6" },
];

/**
 * Slot Machine Arcade Frame Theme Tokens (Consistent with Platform Card Design)
 */
export const SLOT_MACHINE_FRAME_THEME = {
  bg: "#ffffff",
  headerBg: "#f8fafc",
  border: "1px solid #e2e8f0",
  boxShadow: customShadows.cardFlat || "0 4px 20px rgba(0, 0, 0, 0.05)",
  borderRadius: { xs: borderRadiusPx.lg || "16px", sm: borderRadiusPx.xl || "20px" },
  headerBorderRadius: borderRadiusPx.md || "8px",
  goldBorder: "#eab308",
  goldText: "#b45309",
  amberAccent: "#f59e0b",
};

/**
 * 5-SlotBET Game Configuration
 * Fiat & Crypto 5-Symbol Editions (Contract Mapped 0..4)
 */
export const FIVE_SLOT_CONFIG = {
  name: "5-SlotBET",
  subtitle: "5-REEL PROGRESSIVE SLOT",
  reels: 5,
  rows: 3,
  paylines: 1,
  paylineDescription: "Match 3 or more symbols left-to-right on the center payline to win!",
  initialReels: ["bell", "diamond", "crown", "cherries", "lucky_7"],
  defaultBet: 5.0,
  minBet: 1.0,
  maxBet: 500.0,
  betSteps: [1, 2, 5, 10, 25, 50, 100, 250, 500],
  currencySymbol: "BET",
  currencyName: "BET",
};

export const JACKPOT_DEFAULTS = {
  mega: 128900.0,
  major: 16450.0,
  mini: 2840.5,
};

export const THEME_OPTIONS = [
  {
    id: "fiat",
    label: "High-Roller Vegas Slot",
    description: "Authentic casino symbols: Golden Bell (Mini), Brilliant Diamond (Major), Royal Crown (Mega), Casino Cherries (Free Spins), Lucky 7 (Super Spins), Lucky Clover (High Value), Golden Coin (Top Symbol)",
  },
  {
    id: "crypto",
    label: "Crypto 5-Reel Slot",
    description: "Authentic 3D crypto coins: Polygon POL (Mini), Ethereum ETH (Major), Bitcoin BTC (Mega), Solana SOL (Free Spins), Tether USDT (Super Spins), Binance BNB (High Value), AutoBET Gold Token (Top Symbol)",
  },
];

/**
 * FIAT Slot 7-Symbol Set (Contract Mapped 0 to 6)
 */
export const FIAT_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "bell",
    label: "Golden Bell",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Bet / Mini Jackpot",
    code: "BELL",
    symbolText: "🔔",
    iconText: "🔔",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#eab308",
    color: "#ca8a04",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.bell,
    payout: "Mini Jackpot (5x = Mini Jackpot, 4x = 25x, 3x = 10x)",
    description: "Match 5 Golden Bells to trigger the Mini Progressive Jackpot pool!",
  },
  {
    contractNumber: 1,
    id: "diamond",
    label: "Brilliant Diamond",
    winningTier: "Major Jackpot",
    winOutcome: "Major Jackpot",
    code: "DIAMOND",
    symbolText: "💎",
    iconText: "💎",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#2563eb",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.diamond,
    payout: "Major Jackpot (5x = Major Jackpot, 4x = 50x, 3x = 20x)",
    description: "Match 5 Brilliant Diamonds to trigger the Major Progressive Jackpot pool!",
  },
  {
    contractNumber: 2,
    id: "crown",
    label: "Royal Crown",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Jackpot",
    code: "CROWN",
    symbolText: "👑",
    iconText: "👑",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.crown,
    payout: "Mega Jackpot (5x = Grand Mega Jackpot, 4x = 100x, 3x = 35x)",
    description: "Match 5 Royal Crowns to trigger the Grand Mega Progressive Jackpot pool!",
  },
  {
    contractNumber: 3,
    id: "cherries",
    label: "Casino Cherries",
    winningTier: "Free Spins",
    winOutcome: "5-15 Free Spins",
    code: "CHERRY",
    symbolText: "🍒",
    iconText: "🍒",
    multiplier: 15,
    tier: "Free Spins",
    tierColor: "#ef4444",
    color: "#dc2626",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.cherries,
    payout: "5x = 15 Spins, 4x = 10 Spins, 3x = 5 Spins",
    description: "Match Casino Cherries to trigger Free Spins Bonus Rounds!",
  },
  {
    contractNumber: 4,
    id: "lucky_7",
    label: "Lucky 7",
    winningTier: "Super Spins",
    winOutcome: "10-25 Super Spins",
    code: "SEVEN",
    symbolText: "7️⃣",
    iconText: "7️⃣",
    multiplier: 25,
    tier: "Super Spins",
    tierColor: "#dc2626",
    color: "#b91c1c",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.lucky_7,
    payout: "5x = 25 Super Spins, 4x = 15 Super Spins, 3x = 8 Super Spins",
    description: "Match Lucky 7s for Super Free Spins Bonus Rounds!",
  },
  {
    contractNumber: 5,
    id: "clover",
    label: "Lucky Clover",
    winningTier: "High Value",
    winOutcome: "High Value Win",
    code: "CLOVER",
    symbolText: "🍀",
    iconText: "🍀",
    multiplier: 20,
    tier: "High Value",
    tierColor: "#10b981",
    color: "#059669",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.clover,
    payout: "High value winning payline combinations",
    description: "Match Lucky 4-Leaf Clovers for high-value payouts across paylines!",
  },
  {
    contractNumber: 6,
    id: "coin",
    label: "Golden Coin",
    winningTier: "Top Symbol",
    winOutcome: "Top Symbol Win",
    code: "COIN",
    symbolText: "🪙",
    iconText: "🪙",
    multiplier: 35,
    tier: "Top Symbol",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.coin,
    payout: "Top payout symbol across active paylines",
    description: "AutoBET Golden Coin top payout symbol across all 5 reels!",
  },
];

/**
 * CRYPTO Slot 7-Symbol Set (Contract Mapped 0 to 6)
 */
export const CRYPTO_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "pol",
    label: "Polygon POL",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Bet / Mini Jackpot",
    code: "POL",
    symbolText: "🟣",
    iconText: "🟣",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#a855f7",
    color: "#9333ea",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.pol,
    payout: "Mini Jackpot (5x = Mini Jackpot, 4x = 25x, 3x = 10x)",
    description: "Match 5 POL coins to trigger the Mini Progressive Jackpot pool!",
  },
  {
    contractNumber: 1,
    id: "eth",
    label: "Ethereum ETH",
    winningTier: "Major Jackpot",
    winOutcome: "Major Jackpot",
    code: "ETH",
    symbolText: "🔷",
    iconText: "🔷",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#2563eb",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.eth,
    payout: "Major Jackpot (5x = Major Jackpot, 4x = 50x, 3x = 20x)",
    description: "Match 5 Ethereum coins to trigger the Major Progressive Jackpot pool!",
  },
  {
    contractNumber: 2,
    id: "btc",
    label: "Bitcoin BTC",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Jackpot",
    code: "BTC",
    symbolText: "₿",
    iconText: "₿",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.btc,
    payout: "Mega Jackpot (5x = Grand Mega Jackpot, 4x = 100x, 3x = 35x)",
    description: "Match 5 Bitcoins to trigger the Grand Mega Progressive Jackpot pool!",
  },
  {
    contractNumber: 3,
    id: "sol",
    label: "Solana SOL",
    winningTier: "Free Spins",
    winOutcome: "5-15 Free Spins",
    code: "SOL",
    symbolText: "🟣",
    iconText: "🟣",
    multiplier: 15,
    tier: "Free Spins",
    tierColor: "#06b6d4",
    color: "#0891b2",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.sol,
    payout: "5x = 15 Spins, 4x = 10 Spins, 3x = 5 Spins",
    description: "Match 3+ Solana coins to trigger Web3 Free Spins Bonus Round!",
  },
  {
    contractNumber: 4,
    id: "usdt",
    label: "Tether USDT",
    winningTier: "Super Spins",
    winOutcome: "10-25 Super Spins",
    code: "USDT",
    symbolText: "🟢",
    iconText: "🟢",
    multiplier: 25,
    tier: "Super Spins",
    tierColor: "#10b981",
    color: "#059669",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.usdt,
    payout: "5x = 25 Super Spins, 4x = 15 Super Spins, 3x = 8 Super Spins",
    description: "Match 3+ Tether USDT coins for Super Free Spins Bonus Rounds!",
  },
  {
    contractNumber: 5,
    id: "bnb",
    label: "Binance BNB",
    winningTier: "High Value",
    winOutcome: "High Value Win",
    code: "BNB",
    symbolText: "🟡",
    iconText: "🟡",
    multiplier: 20,
    tier: "High Value",
    tierColor: "#eab308",
    color: "#ca8a04",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.bnb,
    payout: "High value winning payline combinations",
    description: "Match Binance BNB coins for high-yield Web3 rewards!",
  },
  {
    contractNumber: 6,
    id: "autobet",
    label: "AutoBET Gold Token",
    winningTier: "Top Symbol",
    winOutcome: "Top Symbol Win",
    code: "AUTOBET",
    symbolText: "🪙",
    iconText: "🪙",
    multiplier: 35,
    tier: "Top Symbol",
    tierColor: "#ec4899",
    color: "#db2777",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.autobet,
    payout: "Top payout symbol across active paylines",
    description: "AutoBET Gold Token top payout symbol across all 5 reels!",
  },
];

export const ALL_SYMBOLS = [...FIAT_SET_SYMBOLS, ...CRYPTO_SET_SYMBOLS];
export const SYMBOLS = ALL_SYMBOLS;

export const DUMMY_BET_CURRENCY = "BET";
export const REEL_COUNT = 5;
export const VISIBLE_ROWS = 3;
export const REEL_STOP_TIMES = [700, 1100, 1500, 1900, 2300];
export const SPIN_TICK_MS = 80;

/**
 * Helper to get the 5 symbols mapped from 0 to 4 based on Fiat or Crypto category
 */
export const getSymbolsByCategory = (category = "fiat") => {
  if (category === "crypto") {
    return CRYPTO_SET_SYMBOLS;
  }
  return FIAT_SET_SYMBOLS;
};

export const getSymbolsByTheme = (themeName = "fiat") => {
  return getSymbolsByCategory(themeName);
};

export const getRandomSymbol = (category = "fiat") => {
  const symbolSet = getSymbolsByCategory(category);
  const randomIndex = Math.floor(Math.random() * symbolSet.length);
  return symbolSet[randomIndex] || symbolSet[0];
};

export const getSymbolById = (id) => {
  return ALL_SYMBOLS.find((symbol) => symbol.id === id) || FIAT_SET_SYMBOLS[0];
};

/**
 * Determine winning payout and jackpot status for 5-Reel Slot
 * Evaluates center 5 symbols (5-of-a-kind, 4-of-a-kind, 3-of-a-kind left-to-right)
 */
export const calculateWinResult = (
  reels,
  betAmount = 5.0,
  isFreeSpins = false,
  jackpots = JACKPOT_DEFAULTS
) => {
  if (!reels || reels.length !== 5) {
    return { isWin: false, amount: 0, type: "none", label: "No Spin", winningIndices: [] };
  }

  const multiplierMultiplier = isFreeSpins ? 2 : 1; // 2x boost during Free Spins

  // Count consecutive matches from left to right starting at reel 0
  const firstSym = reels[0];
  let consecutiveCount = 1;
  const winningIndices = [0];

  for (let i = 1; i < reels.length; i++) {
    const sym = reels[i];
    const isMatch =
      (firstSym.contractNumber !== undefined && firstSym.contractNumber === sym.contractNumber) ||
      (firstSym.id && firstSym.id === sym.id);

    if (isMatch) {
      consecutiveCount++;
      winningIndices.push(i);
    } else {
      break;
    }
  }

  // Also check if any other 3-of-a-kind or 4-of-a-kind exists across the 5 reels if left-to-right is < 3
  if (consecutiveCount < 3) {
    // Check frequency count
    const counts = {};
    reels.forEach((r, idx) => {
      const key = r.id;
      if (!counts[key]) counts[key] = { count: 0, sym: r, indices: [] };
      counts[key].count++;
      counts[key].indices.push(idx);
    });

    let bestKey = null;
    let maxFreq = 0;
    Object.keys(counts).forEach((k) => {
      if (counts[k].count > maxFreq) {
        maxFreq = counts[k].count;
        bestKey = k;
      }
    });

    if (maxFreq >= 3) {
      const best = counts[bestKey];
      return evaluateMatchCount(best.sym, maxFreq, betAmount, multiplierMultiplier, jackpots, best.indices);
    }

    return {
      isWin: false,
      amount: 0,
      type: "loss",
      jackpotTier: null,
      label: "No Match",
      winningIndices: [],
    };
  }

  return evaluateMatchCount(firstSym, consecutiveCount, betAmount, multiplierMultiplier, jackpots, winningIndices);
};

function evaluateMatchCount(symbol, count, betAmount, multiplierMultiplier, jackpots, winningIndices) {
  const symId = symbol.id;
  const contractNum = symbol.contractNumber;

  // 1. 5-OF-A-KIND (TOP JACKPOTS & MAX BONUSES)
  if (count === 5) {
    // MEGA JACKPOT (Crown / BTC)
    if (contractNum === 2 || symId === "crown" || symId === "jackpot_crown" || symId === "btc") {
      const winAmt = jackpots?.mega && jackpots.mega > 0 ? jackpots.mega : betAmount * 500 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mega",
        matchCount: 5,
        label: "👑 MEGA JACKPOT (5x)!",
        symbol,
        winningIndices,
      };
    }

    // MAJOR JACKPOT (Diamond / ETH)
    if (contractNum === 1 || symId === "diamond" || symId === "eth" || symId === "eth_crystal" || symId === "gold_nugget") {
      const winAmt = jackpots?.major && jackpots.major > 0 ? jackpots.major : betAmount * 250 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "major",
        matchCount: 5,
        label: "💎 MAJOR JACKPOT (5x)!",
        symbol,
        winningIndices,
      };
    }

    // MINI JACKPOT (Bell / POL)
    if (contractNum === 0 || symId === "bell" || symId === "pol" || symId === "emerald" || symId === "cherry") {
      const winAmt = jackpots?.mini && jackpots.mini > 0 ? jackpots.mini : betAmount * 100 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mini",
        matchCount: 5,
        label: "🔔 MINI JACKPOT (5x)!",
        symbol,
        winningIndices,
      };
    }

    // 15 Free Spins (Cherries / SOL)
    if (contractNum === 3 || symId === "cherries" || symId === "cherry" || symId === "sol" || symId === "ruby" || symId === "kundu_drum") {
      const winAmt = betAmount * 50 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 15,
        matchCount: 5,
        label: "15 FREE SPINS + 50X!",
        jackpotTier: null,
        symbol,
        winningIndices,
      };
    }

    // 25 Super Spins (Lucky 7 / USDT / Orb)
    if (contractNum === 4 || symId === "lucky_7" || symId === "seven" || symId === "usdt" || symId === "amethyst" || symId === "kumul_bird" || symId === "jackpot_orb") {
      const winAmt = betAmount * 100 * 2 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 25,
        matchCount: 5,
        label: "25 SUPER SPINS (2X WIN) + 100X!",
        jackpotTier: null,
        symbol,
        winningIndices,
      };
    }

    // High Value (#5: Clover / BNB)
    if (contractNum === 5 || symId === "clover" || symId === "bnb" || symId === "kina_shell") {
      const winAmt = betAmount * 50 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match5",
        jackpotTier: null,
        matchCount: 5,
        label: `5x ${symbol.label ? symbol.label.toUpperCase() : "HIGH VALUE"} (50X WIN)`,
        symbol,
        winningIndices,
      };
    }

    // Top Symbol (#6: Golden Coin / AutoBET)
    if (contractNum === 6 || symId === "coin" || symId === "autobet" || symId === "autobet_logo" || symId === "autobet_logo_crypto") {
      const winAmt = betAmount * 75 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match5",
        jackpotTier: null,
        matchCount: 5,
        label: `5x ${symbol.label ? symbol.label.toUpperCase() : "TOP SYMBOL"} (75X WIN)`,
        symbol,
        winningIndices,
      };
    }

    // Fallback 5-of-a-kind
    const winAmt = betAmount * (symbol.multiplier ? symbol.multiplier * 2.5 : 50) * multiplierMultiplier;
    return {
      isWin: true,
      amount: winAmt,
      type: "match5",
      jackpotTier: null,
      matchCount: 5,
      label: `5x ${symbol.label ? symbol.label.toUpperCase() : "WIN"}`,
      symbol,
      winningIndices,
    };
  }

  // 2. 4-OF-A-KIND (TIER 2 WINS)
  if (count === 4) {
    if (contractNum === 2 || symId === "crown" || symId === "jackpot_crown" || symId === "btc") {
      const winAmt = betAmount * 75 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match4",
        jackpotTier: null,
        matchCount: 4,
        label: `4x ${symbol.label.toUpperCase()} (75X WIN)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 1 || symId === "diamond" || symId === "eth" || symId === "eth_crystal" || symId === "gold_nugget") {
      const winAmt = betAmount * 50 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match4",
        jackpotTier: null,
        matchCount: 4,
        label: `4x ${symbol.label.toUpperCase()} (50X WIN)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 0 || symId === "bell" || symId === "pol" || symId === "emerald" || symId === "cherry") {
      const winAmt = betAmount * 25 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match4",
        jackpotTier: null,
        matchCount: 4,
        label: `4x ${symbol.label.toUpperCase()} (25X WIN)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 3 || symId === "cherries" || symId === "cherry" || symId === "sol" || symId === "ruby" || symId === "kundu_drum") {
      const winAmt = betAmount * 25 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 10,
        matchCount: 4,
        label: "10 FREE SPINS + 25X!",
        jackpotTier: null,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 4 || symId === "lucky_7" || symId === "seven" || symId === "usdt" || symId === "amethyst" || symId === "kumul_bird" || symId === "jackpot_orb") {
      const winAmt = betAmount * 50 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 15,
        matchCount: 4,
        label: "15 SUPER SPINS (2X WIN) + 50X!",
        jackpotTier: null,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 5 || symId === "clover" || symId === "bnb" || symId === "kina_shell") {
      const winAmt = betAmount * 25 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match4",
        jackpotTier: null,
        matchCount: 4,
        label: `4x ${symbol.label.toUpperCase()} (25X WIN)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 6 || symId === "coin" || symId === "autobet" || symId === "autobet_logo" || symId === "autobet_logo_crypto") {
      const winAmt = betAmount * 35 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match4",
        jackpotTier: null,
        matchCount: 4,
        label: `4x ${symbol.label.toUpperCase()} (35X WIN)`,
        symbol,
        winningIndices,
      };
    }

    // Fallback 4x
    const winAmt = betAmount * (symbol.multiplier ? symbol.multiplier * 1.5 : 20) * multiplierMultiplier;
    return {
      isWin: true,
      amount: winAmt,
      type: "match4",
      jackpotTier: null,
      matchCount: 4,
      label: `4x ${symbol.label.toUpperCase()}`,
      symbol,
      winningIndices,
    };
  }

  // 3. 3-OF-A-KIND (TIER 3 WINS)
  if (count === 3) {
    if (contractNum === 2 || symId === "crown" || symId === "jackpot_crown" || symId === "btc") {
      const winAmt = betAmount * 25 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match3",
        jackpotTier: null,
        matchCount: 3,
        label: `3x ${symbol.label.toUpperCase()} (25X)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 1 || symId === "diamond" || symId === "eth" || symId === "eth_crystal" || symId === "gold_nugget") {
      const winAmt = betAmount * 15 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match3",
        jackpotTier: null,
        matchCount: 3,
        label: `3x ${symbol.label.toUpperCase()} (15X)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 0 || symId === "bell" || symId === "pol" || symId === "emerald" || symId === "cherry") {
      const winAmt = betAmount * 10 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match3",
        jackpotTier: null,
        matchCount: 3,
        label: `3x ${symbol.label.toUpperCase()} (10X)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 3 || symId === "cherries" || symId === "cherry" || symId === "sol" || symId === "ruby" || symId === "kundu_drum") {
      const winAmt = betAmount * 10 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 5,
        matchCount: 3,
        label: "5 FREE SPINS + 10X!",
        jackpotTier: null,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 4 || symId === "lucky_7" || symId === "seven" || symId === "usdt" || symId === "amethyst" || symId === "kumul_bird" || symId === "jackpot_orb") {
      const winAmt = betAmount * 20 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 8,
        matchCount: 3,
        label: "8 SUPER SPINS (2X WIN) + 20X!",
        jackpotTier: null,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 5 || symId === "clover" || symId === "bnb" || symId === "kina_shell") {
      const winAmt = betAmount * 10 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match3",
        jackpotTier: null,
        matchCount: 3,
        label: `3x ${symbol.label.toUpperCase()} (10X)`,
        symbol,
        winningIndices,
      };
    }

    if (contractNum === 6 || symId === "coin" || symId === "autobet" || symId === "autobet_logo" || symId === "autobet_logo_crypto") {
      const winAmt = betAmount * 15 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "match3",
        jackpotTier: null,
        matchCount: 3,
        label: `3x ${symbol.label.toUpperCase()} (15X)`,
        symbol,
        winningIndices,
      };
    }

    // Default 3x
    const winAmt = betAmount * (symbol.multiplier || 10) * multiplierMultiplier;
    return {
      isWin: true,
      amount: winAmt,
      type: "match3",
      jackpotTier: null,
      matchCount: 3,
      label: `3x ${symbol.label.toUpperCase()}`,
      symbol,
      winningIndices,
    };
  }

  return {
    isWin: false,
    amount: 0,
    type: "loss",
    jackpotTier: null,
    label: "No Match",
    winningIndices: [],
  };
}
